import { createInstance, createLogger, INFO, WARN, ERROR, createPollingProjectConfigManager, createBatchEventProcessor, DEBUG, createStaticProjectConfigManager } from '@optimizely/optimizely-sdk';
import getOptimizelyConfig from './getOptimizelyConfig.js';
const twoSecondsMS = 2000;
let serverOptimizelyClientInstance;
let browserOptimizelyClientInstance;
let dataFileCache;
const getLogLevel = level => {
  const logLevelPreset = {
    warn: WARN,
    warning: WARN,
    error: ERROR,
    info: INFO,
    debug: DEBUG
  };
  return logLevelPreset[level] || INFO;
};
export const updateOptimizelyDataFile = dataFile => {
  const optimizelyConfig = getOptimizelyConfig();
  if (!serverOptimizelyClientInstance) {
    const instance = browserOptimizelyClientInstance;
    const currentDataFileRevision = instance && instance.getOptimizelyConfig().revision;
    if (dataFile.revision !== currentDataFileRevision) {
      if (instance) {
        instance.close();
      }
      const logger = createLogger({
        level: getLogLevel(optimizelyConfig.logLevel)
      });
      const browserProjectConfigManager = createStaticProjectConfigManager({
        datafile: dataFile
      });
      const browserBatchEventProcessor = createBatchEventProcessor({
        batchSize: optimizelyConfig.eventBatchSize,
        flushInterval: optimizelyConfig.eventFlushInterval
      });
      browserOptimizelyClientInstance = createInstance({
        projectConfigManager: browserProjectConfigManager,
        eventProcessor: browserBatchEventProcessor,
        logger
      });
    }
  }
};
const updateDataFileCache = async () => {
  const optimizelyConfig = getOptimizelyConfig();
  const newDataFile = serverOptimizelyClientInstance.projectConfigManager.datafileManager.get();
  const logger = createLogger({
    level: getLogLevel(optimizelyConfig.logLevel)
  });
  const fakeInstanceProjectConfigManager = createStaticProjectConfigManager({
    datafile: newDataFile
  });
  const fakeBatchEventProcessor = createBatchEventProcessor({
    batchSize: optimizelyConfig.eventBatchSize,
    flushInterval: optimizelyConfig.eventFlushInterval
  });
  const fakeInstance = createInstance({
    projectConfigManager: fakeInstanceProjectConfigManager,
    eventProcessor: fakeBatchEventProcessor,
    logger
  });
  try {
    await fakeInstance.onReady({
      timeout: twoSecondsMS
    });
    dataFileCache = newDataFile;
  } catch (readyError) {
    // eslint-disable-next-line no-console
    console.log('Optimizely datafile could not be validated:', readyError.message);
  } finally {
    fakeInstance.close();
  }
};
export const createOptimizelyInstance = async () => {
  const optimizelyConfig = getOptimizelyConfig();
  // Logging options are 'notset', 'debug', 'info', 'warn' (='warning'), 'warning', and 'error'.
  // Default log level is 'error'.
  // https://docs.developers.optimizely.com/feature-experimentation/docs/customize-the-javascript-sdk-logger
  const logger = createLogger({
    level: getLogLevel(optimizelyConfig.logLevel)
  });
  if (!serverOptimizelyClientInstance) {
    const potentialProjectConfigManager = createPollingProjectConfigManager({
      // sdkKey is required because the Optimizely instance will ignore autoUpdate if sdkKey is missing
      // (it thinks it needs it because the urlTemplate is allowed to contain a token that gets replaced by the
      // sdkKey)
      sdkKey: optimizelyConfig.sdkKey,
      autoUpdate: true,
      updateInterval: optimizelyConfig.instanceRefreshInterval,
      urlTemplate: optimizelyConfig.dataFilePath
    });
    const batchEventProcessor = createBatchEventProcessor({
      batchSize: optimizelyConfig.eventBatchSize,
      flushInterval: optimizelyConfig.eventFlushInterval
    });
    const potentialInstance = createInstance({
      projectConfigManager: potentialProjectConfigManager,
      eventProcessor: batchEventProcessor,
      logger
    });
    try {
      await potentialInstance.onReady({
        timeout: twoSecondsMS
      });
      serverOptimizelyClientInstance = potentialInstance;

      // These next two lines keep `dataFileCache` up-to-date.
      serverOptimizelyClientInstance.projectConfigManager.onUpdate(updateDataFileCache);
      await updateDataFileCache();
    } catch (readyError) {
      // eslint-disable-next-line no-console
      console.error('Error initializing Optimizely:', readyError.message);
      return undefined;
    }
  }
  return serverOptimizelyClientInstance;
};

// TODO: remove this line once a datafile accessor is provided by the SDK. Also remove uses of dataFileCache above.
export const getOptimizelyDataFile = () => typeof dataFileCache === 'string' ? JSON.parse(dataFileCache) : dataFileCache;
const getOptimizelyInstance = () => serverOptimizelyClientInstance || browserOptimizelyClientInstance;
export default getOptimizelyInstance;